import React, { useState, useEffect } from "react";
import Tabs from "../components/common/tabs";
import Card from "../components/common/card";
import Badge from "../components/common/badge";
import Button from "../components/common/button";
import { FaWineGlassEmpty } from "react-icons/fa6";

import {
  CheckCircle,
  Lock,
  ArrowRight,
  Building2,
  X,
  Download,
  FileText,
  CheckCircle2,
  Clock,
  Play,
  TrendingUp,
  Utensils,
  ChevronDown,
  RotateCcw,
  ChevronUp,
  Beef,
  Wine,
  Crown,
} from "lucide-react";
import WineQuizPanel from "../components/common/WineQuizPanel";
import FoodQuizPanel from "../components/common/FoodQuizPanel";
import {
  getEmployeeLessons,
  getRestaurants,
  getLessonProgressByRestaurant,
} from "../services/lessonProgress";
import { getUserProgress } from "../services/progress";
import authService from "../services/authService";
import RestaurantSelectModal from "../components/common/RestaurantSelectModal";
import { useNavigate, useLocation } from "react-router-dom";
import FoodRestrictionsIntro from "./FoodRestrictionsIntro";
import FoodAllergensIntro from "./FoodAllergensIntro";
import FoodAccommodationsIntro from "./FoodAccommodationsIntro";
import WineStylesIntro from "./WineStylesIntro";

const ChapterStatus = ({ status, unitLocked, onStart }) => {
  const userRole = localStorage.getItem("userRole");
  if (status === "completed" && unitLocked === false)
    return (
      <Badge
        variant="success"
        className="flex items-center gap-1 bg-yellow-200 text-black px-2 py-2 text-sm font-medium"
      >
        <CheckCircle size={16} className="text-black" />{" "}
        <span className="text-black"> Completed </span>
      </Badge>
    );
  if (status === "start" && unitLocked === false)
    return (
      <Button
        size="sm"
        className="flex items-center gap-1  text-white rounded px-3 py-1 text-sm font-semibold hover:bg-primary/80"
        variant="primary"
        onClick={onStart}
      >
        <ArrowRight size={16} className="text-white" /> Start
      </Button>
    );
  if (status === "locked" && unitLocked === false)
    return (
      <Badge
        variant="default"
        className="flex items-center gap-1 bg-yellow-200 text-gray-600 px-2 py-0.5 text-sm font-medium"
      >
        <Lock size={16} className="text-gray-600" /> Locked
      </Badge>
    );
  return null;
};

const UnitCard = ({ unit, desc, chapters, locked, type, onStartQuiz, lessonStatus }) => {
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole");
  const [isExpanded, setIsExpanded] = useState(true);

  const totalQuestions = chapters.reduce(
    (acc, ch) => acc + (ch.questions?.length || 0),
    0
  );
  const completedQuestions = chapters.reduce(
    (acc, ch) => acc + (ch.attemptedQuestions?.length || 0),
    0
  );

  const isUpdating = lessonStatus?.status === "processing" && (
    lessonStatus.updating_type === (type === "food" ? "dish" : "wine") ||
    lessonStatus.updating_type === "none"
  );

  return (
    <div className="relative mb-6">
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
        {/* Unit Header */}
        <div
          className="p-6 flex items-center justify-between cursor-pointer hover:bg-gray-50/50 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-blue-50 text-blue-600">
              {type === "food" ? <Beef size={24} /> : <FaWineGlassEmpty size={24} />}
            </div>
            <div className="text-left">
              <h3 className="text-lg font-black text-[#111827] tracking-tight">
                {unit}
              </h3>
              <p className="text-[12px] font-bold text-gray-400">
                {chapters.length} chapters • {totalQuestions} questions total
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-gray-50 px-4 py-1.5 rounded-xl text-sm font-black text-[#111827]">
              {completedQuestions}/{totalQuestions}
            </div>
            {isExpanded ? (
              <ChevronUp size={20} className="text-gray-400" />
            ) : (
              <ChevronDown size={20} className="text-gray-400" />
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="p-6 pt-0 space-y-3">
            {chapters.map((ch, idx) => {
              const isCompleted =
                ch.status === "completed" || ch.status === "Completed";
              const isLocked = ch.status === "locked" || locked;
              const isStart = ch.status === "start" && !locked;
              const progressPercentage = Math.round(
                ((ch.attemptedQuestions?.length || 0) /
                  (ch.questions?.length || 1)) *
                100
              );

              return (
                <div
                  key={idx}
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-3xl p-4 transition-all border-2 ${isCompleted
                    ? "bg-[#E6F7ED] border-[#B7E4C7]"
                    : isStart
                      ? "bg-[#FAF7ED] border-[#F3E8E0]"
                      : "bg-gray-50 border-transparent opacity-60"
                    }`}
                >
                  <div className="flex items-center gap-4 text-left flex-grow min-w-0">
                    {/* Status Icon */}
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCompleted
                        ? "bg-[#2D6A4F] text-white"
                        : isStart
                          ? "bg-white text-[#111827] border border-gray-100 shadow-sm"
                          : "bg-gray-200 text-gray-400"
                        }`}
                    >
                      {isCompleted ? (
                        <CheckCircle2 size={20} />
                      ) : (
                        <span className="text-sm font-black">{ch.chapter}</span>
                      )}
                    </div>

                    <div className="flex-grow">
                      <h4
                        className={`text-sm font-black ${isLocked ? "text-gray-400" : "text-[#111827]"
                          }`}
                      >
                        Chapter {ch.chapter}
                      </h4>
                      <p
                        className={`text-[12px] font-bold ${isLocked ? "text-gray-400" : "text-gray-500"
                          }`}
                      >
                        {ch.title}
                      </p>
                      {isStart && !isCompleted && (
                        <div className="mt-2 h-1.5 w-full max-w-[120px] bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#881337] rounded-full"
                            style={{ width: `${progressPercentage}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto justify-between sm:justify-end">
                    {/* Score/Progress Badge */}
                    {!isUpdating && <div className="bg-white px-3 py-1 rounded-xl text-[12px] font-black text-gray-500 border border-gray-100 whitespace-nowrap">
                      {ch?.attemptedQuestions?.length || 0}/
                      {ch.questions?.length || 0}
                    </div>}

                    {/* Action Button */}
                    {isCompleted ? (
                      <button
                        onClick={() => onStartQuiz(type, unit, ch.title, true)}
                        disabled={isUpdating}
                        className={`flex items-center gap-2 px-6 py-2 rounded-xl border-2 border-[#881337] text-[#881337] font-black text-xs transition-all shadow-sm active:scale-95 ${isUpdating
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-[#881337] hover:text-white"
                          }`}
                      >
                        <RotateCcw size={14} className={isUpdating ? "animate-spin" : ""} />
                        {isUpdating ? "Updating..." : "Retake"}
                      </button>
                    ) : isStart ? (
                      <button
                        onClick={() => onStartQuiz(type, unit, ch.title, false)}
                        disabled={isUpdating}
                        className={`flex items-center gap-2 px-6 py-2 rounded-xl bg-[#881337] text-white font-black text-xs transition-all shadow-sm active:scale-95 ${isUpdating
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-[#6e0f2c]"
                          }`}
                      >
                        {isUpdating ? (
                          <>
                            <RotateCcw size={14} className="animate-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            Continue
                            <ArrowRight size={14} />
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="p-2 text-gray-300">
                        <Lock size={18} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Layover for locked units */}
        {locked && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/5 backdrop-blur-[2px] z-10 rounded-[2rem]">
            <div className="bg-white p-8 rounded-[2.5rem] shadow-2xl text-center max-w-[380px] mx-4 border border-white/50">
              <div className="w-16 h-16 bg-[#FEF3D7] rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                <Crown size={28} className="text-[#D97706]" fill="#D97706" />
              </div>
              <h4 className="text-xl font-black text-[#111827] mb-3 leading-tight">
                You are on Free Trial
              </h4>
              <p className="text-[14px] text-gray-500 font-bold mb-8 leading-relaxed">
                Contact your admin to unlock this unit and access all lessons.
              </p>
              <button
                className="w-full py-4 rounded-2xl bg-[#881337] text-white font-black text-sm shadow-lg shadow-[#881337]/20 hover:bg-[#70102d] transition-all active:scale-95"
                onClick={() => navigate("/subscription")}
              >
                Contact Admin
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <div className="text-lg sm:text-xl font-black text-[#111827] mb-4 sm:mb-6 mt-10 text-left w-full tracking-tight">
    {children}
  </div>
);

// Group lessons by unit and unit_name
function groupLessonsByUnit(lessons, category) {
  const units = {};
  lessons
    ?.filter((lesson) => lesson.category.toLowerCase() === category)
    .forEach((lesson) => {
      const key = `${lesson.unit}-${lesson.unit_name}`;
      if (!units[key]) {
        units[key] = {
          unit: lesson.unit,
          unit_name: lesson.unit_name,
          category: lesson.category,
          attemptedQuestions: lesson.attemptedQuestions,
          // Server-computed, restaurant-scoped lock (see entitlementService.mjs)
          // rather than the logged-in user's own subscription state, so this
          // reflects the selected restaurant's entitlement even for a user
          // assigned to multiple restaurants in different subscription states.
          isLocked: Boolean(lesson.isLocked),
          chapters: [],
        };
      }
      units[key].chapters.push({
        chapter: lesson.chapter,
        attemptedQuestions: lesson.attemptedQuestions,
        chapter_name: lesson.chapter_name,
        status: lesson.status.toLowerCase(),
        isNewQuestions: lesson?.isNewQuestions,
        uuid: lesson.uuid,
        questions: lesson.questions,
        lessonObj: lesson, // keep the full lesson for quiz
      });
    });
  // Sort chapters by chapter number
  Object.values(units).forEach((unit) => {
    unit.chapters.sort((a, b) => a.chapter - b.chapter);
  });
  return Object.values(units).sort((a, b) => a.unit - b.unit);
}

export const MyLessons = () => {
  const [selectedTab, setSelectedTab] = useState("all");
  const [quizPanel, setQuizPanel] = useState(null);
  // Bumped on every quiz launch so a retry remounts the panel with fresh internal state
  const [quizLaunchKey, setQuizLaunchKey] = useState(0);
  const [introPanel, setIntroPanel] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [lessons, setLessons] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [user, setUser] = useState({});
  const [isRestaurantModalOpen, setIsRestaurantModalOpen] = useState(false);
  const [openModel, setOpenModal] = useState(false);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [lessonStatus, setLessonStatus] = useState(null);
  const location = useLocation();

  useEffect(() => { }, []);

  useEffect(() => {
    const fetchRestaurants = async () => {
      setRestaurantsLoading(true);
      const currentUser = authService.getCurrentUser();
      if (currentUser.role) {
        setUserRole(currentUser?.role);
      }
      try {
        const data = await getRestaurants("name");

        // Ensure data is an array
        const restaurantsArray = Array.isArray(data) ? data : [];
        setRestaurants(restaurantsArray);

        if (restaurantsArray.length > 0) {
          setSelectedRestaurant(restaurantsArray[0].uuid);
        }
      } catch (error) {
        console.error("Error fetching restaurants:", error);
        setRestaurants([]);
      } finally {
        setRestaurantsLoading(false);
      }
    };

    const fetchProfile = async () => {
      try {
        const profile = await authService.getProfile();
        setUser(profile);
      } catch (error) {
        console.log(error, "error");
      }
    };

    fetchProfile();
    fetchRestaurants();

    // Progress will be fetched when restaurant is selected
  }, [userRole]);

  useEffect(() => {
    if (!selectedRestaurant) {
      return;
    }

    const fetchLessons = async () => {
      setLessonLoading(true);
      try {
        const data = await getEmployeeLessons(selectedRestaurant);
        console.log(data, "data");
        setLessons(data?.lessonsWithStatus);
        setLessonStatus(data?.lessonUpdateStatus);
      } catch (error) {
        console.error("Error fetching lessons:", error);
      } finally {
        setLessonLoading(false);
      }
    };

    const fetchProgress = async () => {
      setStatsLoading(true);
      try {
        // Fetch restaurant-specific progress if not employee
        const data =
          userRole === "employee"
            ? await getUserProgress()
            : await getLessonProgressByRestaurant(selectedRestaurant);
        setProgressData(data);
      } catch (error) {
        console.error("Error fetching progress stats:", error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchLessons();
    fetchProgress();
  }, [selectedRestaurant, userRole]);

  const tabs = [
    { label: "All", value: "all" },
    { label: "Food", value: "food" },
    { label: "Wine", value: "wine" },
  ];

  const filteredFoodLessons = lessons?.filter(
    (lesson) => lesson?.category === "food"
  );
  const filteredWineLessons = lessons?.filter(
    (lesson) => lesson?.category === "wine"
  );

  const processedFoodLessons = filteredFoodLessons?.map((lesson, idx) => {
    let anyFoodFailed = false;
    let status = lesson.status;

    if (userRole === "super_admin") {
      // super admin override
      status = status === "Completed" ? "completed" : "start";
    } else {
      if (idx === 0) {
        if (status !== "Completed") status = "start";
      } else {
        if (
          filteredFoodLessons[idx - 1].status !== "Completed" ||
          anyFoodFailed
        ) {
          status = "locked";
        } else if (status === "Completed") {
          status = "completed";
        } else {
          status = "start";
        }
      }
      if (status !== "Completed") anyFoodFailed = true;
    }

    return { ...lesson, status };
  });

  const processedWineLessons = filteredWineLessons?.map((lesson, idx) => {
    let anyWineFailed = false;
    let status = lesson.status;

    if (userRole === "super_admin") {
      // super admin override
      status = status === "Completed" ? "completed" : "start";
    } else {
      if (idx === 0) {
        if (status !== "Completed") status = "start";
      } else {
        if (
          filteredWineLessons[idx - 1].status !== "Completed" ||
          anyWineFailed
        ) {
          status = "locked";
        } else if (status === "Completed") {
          status = "completed";
        } else {
          status = "start";
        }
      }
      if (status !== "Completed") anyWineFailed = true;
    }

    return { ...lesson, status };
  });

  const groupedFoodUnits = groupLessonsByUnit(processedFoodLessons, "food");
  const groupedWineUnits = groupLessonsByUnit(processedWineLessons, "wine");

  const groupFoodLessons = [groupedFoodUnits];
  const groupWineLessons = [groupedWineUnits];

  // Tabs content for each tab
  const getTabContent = (type) => {
    if (lessonLoading) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-500"></div>
        </div>
      );
    } else if (!lessons || (lessons.length === 0 && !restaurantsLoading)) {
      return (
        <div className="text-center py-8">
          <p className="text-lg text-gray-600">
            There are no lessons available for this restaurant.
          </p>
        </div>
      );
    } else if (restaurantsLoading) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-500"></div>
        </div>
      );
    }

    return (
      <>
        {(type === "all" || type === "food") &&
          groupFoodLessons[0].length !== 0 && (
            <>
              <SectionTitle>Food Units</SectionTitle>
              <div className="space-y-2 mb-10">
                {groupedFoodUnits.map((unit) => (
                  <UnitCard
                    key={unit.unit + unit.unit_name}
                    unit={`Unit ${unit.unit}: ${unit.unit_name}`}
                    desc={unit.chapters
                      .map((ch) => `Chapter ${ch.chapter}: ${ch.chapter_name}`)
                      .join(", ")}
                    type="food"
                    locked={unit?.isLocked === true}
                    chapters={unit.chapters.map((ch) => ({
                      title: ch.chapter_name,
                      chapter: ch.chapter,
                      status: ch.status,
                      isNewQuestions: ch.isNewQuestions,
                      uuid: ch.uuid,
                      attemptedQuestions: ch.attemptedQuestions,
                      questions: ch.questions,
                      lessonObj: ch.lessonObj,
                    }))}
                    onStartQuiz={handleStartQuiz}
                    lessonStatus={lessonStatus}
                  />
                ))}
              </div>
            </>
          )}
        {(type === "all" || type === "wine") &&
          groupWineLessons[0].length > 0 && (
            <>
              <SectionTitle>Wine Units</SectionTitle>
              <div className="space-y-2 mb-10">
                {groupedWineUnits.map((unit) => (
                  <UnitCard
                    key={unit.unit + unit.unit_name}
                    unit={`Unit ${unit.unit}: ${unit.unit_name}`}
                    desc={unit.chapters
                      .map((ch) => `Chapter ${ch.chapter}: ${ch.chapter_name}`)
                      .join(", ")}
                    type="wine"
                    locked={unit?.isLocked === true}
                    chapters={unit.chapters.map((ch) => ({
                      title: ch.chapter_name,
                      chapter: ch.chapter,
                      status: ch.status,
                      uuid: ch.uuid,
                      isNewQuestions: ch.isNewQuestions,
                      attemptedQuestions: ch.attemptedQuestions,
                      questions: ch.questions,
                      lessonObj: ch.lessonObj,
                    }))}
                    onStartQuiz={handleStartQuiz}
                    lessonStatus={lessonStatus}
                  />
                ))}
              </div>
            </>
          )}
      </>
    );
  };

  function handleStartQuiz(type, unit, chapter, isRetake = false) {
    let foundLesson = null;
    for (const l of lessons) {
      if (
        l.category === type &&
        `Unit ${l.unit}: ${l.unit_name}` === unit &&
        l.chapter_name === chapter
      ) {
        foundLesson = l;
        break;
      }
    }

    if (foundLesson) {
      // Check for Intro Pages
      if (type === "food" && foundLesson.unit === 2) {
        if (foundLesson.chapter === 1) {
          setIntroPanel({
            component: FoodRestrictionsIntro,
            lesson: foundLesson,
            type,
            isRetake,
          });
          return;
        } else if (foundLesson.chapter === 2) {
          setIntroPanel({
            component: FoodAllergensIntro,
            lesson: foundLesson,
            type,
            isRetake,
          });
          return;
        } else if (foundLesson.chapter === 3) {
          setIntroPanel({
            component: FoodAccommodationsIntro,
            lesson: foundLesson,
            type,
            isRetake,
          });
          return;
        }
      } else if (
        type === "wine" &&
        foundLesson.unit === 2 &&
        foundLesson.chapter === 1
      ) {
        // Assuming Wine Unit 2 Chapter 1 based on user request, though file content said Unit 1. User said "Wine Unit 2, Chapter 1 Wine Styles". I will trust user request but fallback if unit is 1 in data. Actually, let's check unit 1 too just in case or strictly follow user. User said "Wine Unit 2". I'll stick to that but maybe I should check the lesson content. Let's assume unit 2 as requested.
        setIntroPanel({
          component: WineStylesIntro,
          lesson: foundLesson,
          type,
          isRetake,
        });
        return;
      }

      launchQuiz(foundLesson, type, isRetake);
    }
  }

  function launchQuiz(foundLesson, type, isRetake = false) {
    setQuizLaunchKey((k) => k + 1);
    // If retaking, show all questions. Otherwise, filter out attempted questions
    let questionsToShow;

    if (isRetake) {
      // Retake: Show all questions
      questionsToShow = foundLesson.questions;
    } else {
      // Continue: Show only unattempted questions
      const attemptedIds = (foundLesson.attemptedQuestions || []).map(
        (aq) => aq.uuid
      );
      questionsToShow = foundLesson.questions.filter(
        (q) => !attemptedIds.includes(q.uuid)
      );

      // A chapter that has been fully answered but not passed stays "in progress"
      // (it only flips to "completed" at 80%+), so its card still offers "Continue".
      // Every question is already attempted at that point, so the unattempted-only
      // filter above would open the quiz with zero questions. Fall back to showing
      // the full chapter so the user can retry it instead of hitting an empty quiz.
      if (questionsToShow.length === 0 && foundLesson.questions.length > 0) {
        questionsToShow = foundLesson.questions;
      }
    }

    const processedQuestions = questionsToShow.map((q) => ({
      id: q.uuid,
      _id: q._id,
      question: q.question_text,
      type: q.question_type,
      options: q.options_variable,
      correct: q.correct_answer_variable,
      difficulty: q.difficulty.charAt(0).toUpperCase() + q.difficulty.slice(1),
      hint: q.hint,
      error: "Incorrect. Try again.",
    }));
    if (type === "food") {
      setQuizPanel({
        type: "food",
        unit: `Unit ${foundLesson.unit}: ${foundLesson.unit_name}`,
        chapter: foundLesson.chapter_name,
        questions: processedQuestions,
        content: foundLesson.content,
        difficulty: foundLesson.difficulty,
        lessonId: foundLesson.id,
        lessonUuid: foundLesson.uuid,
        startIndex: 0,
        attemptedQuestions: isRetake ? [] : foundLesson.attemptedQuestions,
        isRetake: isRetake,
      });
    } else if (type === "wine") {
      setQuizPanel({
        type: "wine",
        unit: `Unit ${foundLesson.unit}: ${foundLesson.unit_name}`,
        chapter: foundLesson.chapter_name,
        questions: processedQuestions,
        content: foundLesson.content,
        difficulty: foundLesson.difficulty,
        lessonId: foundLesson.id,
        lessonUuid: foundLesson.uuid,
        startIndex: 0,
        attemptedQuestions: isRetake ? [] : foundLesson.attemptedQuestions,
        isRetake: isRetake,
      });
    }
  }

  // Retry the chapter the learner just failed: re-run every question in that chapter.
  async function handleRetryChapter(type) {
    const lessonUuid = quizPanel?.lessonUuid;
    if (!lessonUuid) return;

    let lessonList = lessons;
    if (selectedRestaurant) {
      try {
        const data = await getEmployeeLessons(selectedRestaurant);
        lessonList = data?.lessonsWithStatus || lessons;
        setLessons(lessonList);
      } catch (error) {
        console.error("Error refreshing lessons before retry:", error);
      }
    }

    const freshLesson = lessonList.find((l) => l.uuid === lessonUuid);
    if (!freshLesson) return;

    launchQuiz(freshLesson, type, true);
  }

  function handleCloseQuiz() {
    setQuizPanel(null);
    setOpenModal(false);
  }

  const getSelectedRestaurantName = () => {
    const restaurant = restaurants.find((r) => r.uuid === selectedRestaurant);
    return restaurant ? restaurant.name : "Select Restaurant";
  };

  const getCompletionStats = () => {
    if (!progressData) {
      return {
        activeUnits: 0,
        completed: 0,
        hoursLearned: "0.0",
        avgScore: 0,
        trends: { units: "+0%", completed: "+0%", hours: "0h", score: "+0%" },
      };
    }

    const completed = progressData.completedLessons || 0;
    const totalLessons = progressData.allLessons || 1;

    // Calculate unique units from allLessonsObject
    const uniqueUnits = progressData.allLessonsObject
      ? [
        ...new Set(
          progressData.allLessonsObject.map((l) => `${l.unit}-${l.unit_name}`)
        ),
      ]
      : [];
    const activeUnitsCount = uniqueUnits.length;

    // Total possible units (this might be hardcoded or derived)
    const totalUnits = activeUnitsCount; // For now use count as base

    // Estimate hours learned: assuming 20 mins per completed lesson
    const totalMinutes = (progressData.completedLessonsByUser || []).reduce(
      (acc, l) => acc + (l.estimated_time || 20),
      0
    );
    const hoursLearned = (totalMinutes / 60).toFixed(1);

    const completionRate = Math.round((completed / totalLessons) * 100);
    const unitProgress = Math.round(
      (activeUnitsCount / (totalUnits || 1)) * 100
    );

    return {
      activeUnits: activeUnitsCount,
      completed,
      hoursLearned: hoursLearned,
      avgScore: Math.round(progressData.averageScore || 0),
      trends: {
        units: `${unitProgress}%`,
        completed: `${completionRate}%`,
        hours: "24h", // Current week's target or similar
        score: `+${Math.round((progressData.averageScore || 0) * 0.1)}%`, // Symbolic trend
      },
    };
  };

  const getFeaturedLesson = () => {
    if (selectedTab === "food") {
      return (
        processedFoodLessons.find((l) => l.status === "start") ||
        processedFoodLessons[0]
      );
    }
    if (selectedTab === "wine") {
      return (
        processedWineLessons.find((l) => l.status === "start") ||
        processedWineLessons[0]
      );
    }
    // For 'all' tab: find any lesson that is currently in "start" status (active/next)
    // Priority: Food active > Wine active > Food first

    console.log(processedFoodLessons);
    console.log(processedWineLessons);
    const activeFood = processedFoodLessons?.find((l) => l.status === "start");
    const activeWine = processedWineLessons?.find((l) => l.status === "start");
    console.log(activeFood, activeWine);
    console.log(processedFoodLessons?.[0]);
    return activeFood || activeWine || processedFoodLessons?.[0];
  };

  const featured = getFeaturedLesson();
  const stats = getCompletionStats();

  if (introPanel) {
    const IntroComponent = introPanel.component;
    const { lesson, type, isRetake } = introPanel;
    const unitTitle = `Unit ${lesson.unit}: ${lesson.unit_name}`;
    const chapterTitle = lesson.chapter_name;

    return (
      <div className="fixed inset-0 z-[100] bg-white overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-[#fcf7ec] sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIntroPanel(null)}
              className="text-gray-500 hover:text-red-500 p-1 rounded-full"
            >
              <X size={22} />
            </button>
          </div>
          <div className="">
            <div className="text-lg sm:text-2xl text-black font-bold">
              {unitTitle}, {chapterTitle}
            </div>
          </div>
          <div></div>
        </div>
        <IntroComponent
          onStart={() => {
            setIntroPanel(null);
            launchQuiz(lesson, type, isRetake);
          }}
        />
      </div>
    );
  }

  return (
    <>
      {/* Top Navigation Bar with Restaurant Selector */}
      {Array.isArray(restaurants) &&
        restaurants.length > 0 && (
          <div className="sticky top-0  bg-transparent backdrop-blur-sm">
            <div className="w-full py-6 flex justify-center">
              <button
                onClick={() => setIsRestaurantModalOpen(true)}
                className="flex items-center justify-between gap-4 px-8 py-3 border-none hover:bg-black/5 rounded-2xl transition-all min-w-[320px] group"
              >
                <div className="flex items-center gap-4 text-left">
                  <div className="p-2.5 rounded-xl bg-gray-100 text-gray-600 group-hover:bg-gray-200 transition-colors shadow-sm">
                    <Building2 size={24} />
                  </div>
                  <div>
                    <div className="text-base font-black text-[#111827] leading-none mb-1.5">
                      {getSelectedRestaurantName()}
                    </div>
                    <div className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                      Current Restaurant
                    </div>
                  </div>
                </div>
                <ChevronDown
                  size={20}
                  className="text-gray-400 group-hover:text-gray-600 transition-colors"
                />
              </button>
            </div>
          </div>
        )}

      <div className="bg-[#FAF7ED] min-h-screen">
        <div className="max-w-7xl w-full mx-auto py-8 px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
            <div className="text-left">
              <h1 className="text-3xl font-black text-[#111827] tracking-tight mb-1">
                My Lessons
              </h1>
              <p className="text-gray-500 font-semibold">
                Continue your culinary education journey
              </p>
            </div>


          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between h-40">
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-blue-50 rounded-lg text-blue-600">
                  <FileText size={20} />
                </div>
                <span className="text-emerald-500 text-start text-xs font-black">
                  {stats.trends.units}
                </span>
              </div>
              <div>
                <div className="text-3xl text-start font-black text-[#111827] mb-1">
                  {statsLoading ? "..." : stats.activeUnits}
                </div>
                <div className="text-xs text-start font-bold text-gray-400">
                  Active Units
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between h-40">
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-emerald-50 rounded-lg text-emerald-600">
                  <CheckCircle2 size={20} />
                </div>
                <span className="text-emerald-500 text-start text-xs font-black">
                  {stats.trends.completed}
                </span>
              </div>
              <div>
                <div className="text-3xl text-start font-black text-[#111827] mb-1">
                  {statsLoading ? "..." : stats.completed}
                </div>
                <div className="text-xs text-start font-bold text-gray-400">
                  Completed
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between h-40">
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-amber-50 rounded-lg text-amber-600">
                  <Clock size={20} />
                </div>
                <span className="text-gray-400 text-start text-xs font-black">
                  {stats.trends.hours}
                </span>
              </div>
              <div>
                <div className="text-3xl text-start font-black text-[#111827] mb-1">
                  {statsLoading ? "..." : stats.hoursLearned}
                </div>
                <div className="text-xs text-start font-bold text-gray-400">
                  Hours Learned
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between h-40">
              <div className="flex justify-between items-start">
                <div className="p-2.5 bg-purple-50 rounded-lg text-purple-600">
                  <TrendingUp size={20} />
                </div>
                <span className="text-emerald-500 text-start text-xs font-black">
                  {stats.trends.score}
                </span>
              </div>
              <div>
                <div className="text-3xl text-start font-black text-[#111827] mb-1">
                  {statsLoading ? "..." : stats.avgScore}%
                </div>
                <div className="text-xs font-bold text-gray-400">
                  Avg. Score
                </div>
              </div>
            </div>
          </div>

          {/* Lesson Update Notification Banner */}
          {lessonStatus?.status === 'processing' && (
            <div className="mb-8 p-6 bg-blue-50 border border-blue-100 rounded-[2rem] flex items-center justify-between gap-4 animate-pulse shadow-sm">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center shadow-inner">
                  <RotateCcw size={24} className="text-blue-600 animate-spin" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-black text-blue-900 leading-tight mb-1">
                    Wait a moment - We're updating your {lessonStatus.updating_type === 'dish' ? 'Food' : lessonStatus.updating_type === 'wine' ? 'Wine' : 'Menu'} Lessons!
                  </h3>
                  <p className="text-sm font-bold text-blue-600/80">
                    Your restaurant {lessonStatus.updating_type === 'dish' ? 'dishes' : lessonStatus.updating_type === 'wine' ? 'wines' : 'menu'} recently changed. We're generating new training materials for you.
                  </p>
                </div>
              </div>
              <div className="hidden sm:block bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider shadow-md">
                Updating {lessonStatus.updating_type === 'dish' ? 'Food' : lessonStatus.updating_type === 'wine' ? 'Wine' : 'Items'}...
              </div>
            </div>
          )}



          {/* Tabs and Content */}
          <div className="mt-4 text-left ">
            <div className="bg-white p-1 text-left rounded-2xl inline-flex mb-8 shadow-sm border border-gray-100">
              {tabs.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setSelectedTab(t.value)}
                  className={`px-8 py-2.5 rounded-xl text-sm font-bold transition-all ${selectedTab === t.value
                    ? "bg-[#881337] text-white shadow-md shadow-[#881337]/10"
                    : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="tab-content transition-all duration-300">
              {getTabContent(selectedTab)}
            </div>
          </div>
        </div>
      </div>
      {quizPanel && quizPanel.type === "food" && (
        <FoodQuizPanel
          key={`food-${quizPanel.lessonUuid}-${quizLaunchKey}`}
          isOpen={true}
          onClose={() => {
            setOpenModal(!openModel);
          }}
          unit={quizPanel.unit}
          chapter={quizPanel.chapter}
          questions={quizPanel.questions}
          content={quizPanel.content}
          difficulty={quizPanel.difficulty}
          lessonId={quizPanel.lessonId}
          lessonUuid={quizPanel.lessonUuid}
          startIndex={quizPanel.startIndex}
          onLessonComplete={async () => {
            setQuizPanel(null);
            // Re-fetch lessons for the current restaurant
            if (selectedRestaurant) {
              const data = await getEmployeeLessons(selectedRestaurant);
              setLessons(data?.lessonsWithStatus);
            }
          }}
          onRetryChapter={() => handleRetryChapter("food")}
          allChaptersForUnit={lessons.filter(
            (l) =>
              l.category === "food" &&
              `Unit ${l.unit}: ${l.unit_name}` === quizPanel.unit
          )}
          menuData={lessons.find((l) => l.uuid === quizPanel.lessonUuid)?.menu}
          attemptedQuestions={quizPanel.attemptedQuestions}
          openModel={openModel}
          setOpenModal={setOpenModal}
          handleCloseQuiz={handleCloseQuiz}
        />
      )}
      {quizPanel && quizPanel.type === "wine" && (
        <WineQuizPanel
          key={`wine-${quizPanel.lessonUuid}-${quizLaunchKey}`}
          isOpen={true}
          onClose={() => setOpenModal(!openModel)}
          unit={quizPanel.unit}
          chapter={quizPanel.chapter}
          questions={quizPanel.questions}
          content={quizPanel.content}
          difficulty={quizPanel.difficulty}
          lessonId={quizPanel.lessonId}
          lessonUuid={quizPanel.lessonUuid}
          startIndex={quizPanel.startIndex}
          onLessonComplete={async () => {
            setQuizPanel(null);
            // Re-fetch lessons for the current restaurant
            if (selectedRestaurant) {
              const data = await getEmployeeLessons(selectedRestaurant);
              setLessons(data?.lessonsWithStatus);
            }
          }}
          onRetryChapter={() => handleRetryChapter("wine")}
          allChaptersForUnit={lessons.filter(
            (l) =>
              l.category === "wine" &&
              `Unit ${l.unit}: ${l.unit_name}` === quizPanel.unit
          )}
          menuData={lessons.find((l) => l.uuid === quizPanel.lessonUuid)?.menu}
          attemptedQuestions={quizPanel.attemptedQuestions}
          openModel={openModel}
          setOpenModal={setOpenModal}
          handleCloseQuiz={handleCloseQuiz}
        />
      )}

      {/* Restaurant Selection Modal */}
      <RestaurantSelectModal
        isOpen={isRestaurantModalOpen}
        onClose={() => setIsRestaurantModalOpen(false)}
        restaurants={restaurants}
        selectedRestaurant={selectedRestaurant}
        onSelect={setSelectedRestaurant}
        isLoading={restaurantsLoading}
      />
    </>
  );
};

export default MyLessons;
